import React, { useMemo } from 'react';
import {
  Box,
  Heading,
  Select,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  Flex,
  Icon,
  Badge,
  Card,
  CardHeader,
  CardBody,
  HStack,
  VStack,
  useColorModeValue,
  SimpleGrid,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { OrderTable } from '.';
import { GetSettledOrdersQuery, Settlement } from 'src/utils/__generated__/graphql';
import { FiCalendar, FiDollarSign, FiPackage, FiShoppingBag, FiTruck } from 'react-icons/fi';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

interface SettledOrdersProps {
  data?: GetSettledOrdersQuery;
  timeRange: string;
  handleTimeRangeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  timeRanges: { label: string; value: string }[];
}

const SettledOrders: React.FC<SettledOrdersProps> = ({ data, timeRange, handleTimeRangeChange, timeRanges }) => {
  const settlements = data?.settlements as Settlement[] | undefined;

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const statBg = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const accentColor = useColorModeValue('brand.500', 'brand.300');
  const selectBg = useColorModeValue('white', 'gray.700');

  const stats = useMemo(() => {
    if (!settlements || settlements.length === 0) {
      return {
        totalOrders: 0,
        totalAmount: 0,
        totalFoodAmount: 0,
        totalDeliveryAmount: 0,
        adminEarning: 0,
        restaurantEarnings: 0,
      };
    }

    return settlements.reduce(
      (acc, settlement) => {
        const orderCount = Number(settlement?.orders_aggregate?.aggregate?.count) || 0;
        const dishAmount = parseFloat(settlement?.dishAmount) || 0;
        const deliveryCharge = parseFloat(settlement?.totalDeliveryCharge) || 0;
        const adminEarning = parseFloat(settlement?.totalAdminEarning) || 0;
        const restaurantEarning = parseFloat(settlement.totalRestaurantEarning) || 0;

        return {
          totalOrders: acc.totalOrders + orderCount,
          totalAmount: acc.totalAmount + dishAmount + deliveryCharge,
          totalFoodAmount: acc.totalFoodAmount + dishAmount,
          totalDeliveryAmount: acc.totalDeliveryAmount + deliveryCharge,
          adminEarning: acc.adminEarning + adminEarning,
          restaurantEarnings: acc.restaurantEarnings + restaurantEarning,
        };
      },
      {
        totalOrders: 0,
        totalAmount: 0,
        totalFoodAmount: 0,
        totalDeliveryAmount: 0,
        adminEarning: 0,
        restaurantEarnings: 0,
      }
    );
  }, [settlements]);

  return (
    <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <Card
        borderRadius="xl"
        boxShadow="sm"
        borderWidth="1px"
        borderColor={borderColor}
        bg={cardBg}
        mb={8}
        overflow="hidden"
      >
        <CardHeader bg={useColorModeValue('gray.50', 'gray.700')} py={4} px={6}>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'start', md: 'center' }}
            gap={4}
          >
            <HStack>
              <Icon as={FiDollarSign as any} color={accentColor} boxSize={5} />
              <Heading as="h4" size="md">
                Settled Orders
              </Heading>
              <Badge colorScheme="green" fontSize="sm" borderRadius="full">
                {settlements?.length || 0} settlements
              </Badge>
            </HStack>

            <InputGroup maxW={{ base: 'full', md: '250px' }}>
              <InputLeftElement pointerEvents="none">
                <Icon as={FiCalendar as any} color={accentColor} />
              </InputLeftElement>
              <Select value={timeRange} onChange={handleTimeRangeChange} bg={selectBg} borderRadius="md" pl={10}>
                {timeRanges.map((range) => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </Select>
            </InputGroup>
          </Flex>
        </CardHeader>

        <CardBody p={6}>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
            <MotionFlex
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Stat
                px={6}
                py={4}
                bg={statBg}
                borderRadius="xl"
                boxShadow="sm"
                border="1px"
                borderColor={borderColor}
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
                transition="all 0.2s"
                w="full"
              >
                <HStack spacing={3} mb={2}>
                  <Icon as={FiShoppingBag as any} color={accentColor} boxSize={5} />
                  <StatLabel fontSize="sm" fontWeight="medium" color={textColor}>
                    Total Orders
                  </StatLabel>
                </HStack>
                <StatNumber fontSize="3xl" fontWeight="bold" color={accentColor}>
                  {stats.totalOrders}
                </StatNumber>
                <StatHelpText>
                  <Text fontSize="sm">Completed transactions</Text>
                </StatHelpText>
              </Stat>
            </MotionFlex>

            <MotionFlex
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Stat
                px={6}
                py={4}
                bg={statBg}
                borderRadius="xl"
                boxShadow="sm"
                border="1px"
                borderColor={borderColor}
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
                transition="all 0.2s"
                w="full"
              >
                <HStack spacing={3} mb={2}>
                  <Icon as={FiDollarSign as any} color="green.500" boxSize={5} />
                  <StatLabel fontSize="sm" fontWeight="medium" color={textColor}>
                    Total Amount
                  </StatLabel>
                </HStack>
                <StatNumber fontSize="3xl" fontWeight="bold" color="green.500">
                  Rs. {stats.totalAmount.toFixed(2)}
                </StatNumber>
                <StatHelpText>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs">Food: Rs. {stats.totalFoodAmount.toFixed(2)}</Text>
                    <Text fontSize="xs">Delivery: Rs. {stats.totalDeliveryAmount.toFixed(2)}</Text>
                  </VStack>
                </StatHelpText>
              </Stat>
            </MotionFlex>

            <MotionFlex
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Stat
                px={6}
                py={4}
                bg={statBg}
                borderRadius="xl"
                boxShadow="sm"
                border="1px"
                borderColor={borderColor}
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
                transition="all 0.2s"
                w="full"
              >
                <HStack spacing={3} mb={2}>
                  <Icon as={FiPackage as any} color="purple.500" boxSize={5} />
                  <StatLabel fontSize="sm" fontWeight="medium" color={textColor}>
                    Admin Earning
                  </StatLabel>
                </HStack>
                <StatNumber fontSize="3xl" fontWeight="bold" color="purple.500">
                  Rs. {stats.adminEarning.toFixed(2)}
                </StatNumber>
                <StatHelpText>
                  <Text fontSize="sm">Platform commission</Text>
                </StatHelpText>
              </Stat>
            </MotionFlex>

            <MotionFlex
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Stat
                px={6}
                py={4}
                bg={statBg}
                borderRadius="xl"
                boxShadow="sm"
                border="1px"
                borderColor={borderColor}
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
                transition="all 0.2s"
                w="full"
              >
                <HStack spacing={3} mb={2}>
                  <Icon as={FiTruck as any} color="blue.500" boxSize={5} />
                  <StatLabel fontSize="sm" fontWeight="medium" color={textColor}>
                    Restaurant Earnings
                  </StatLabel>
                </HStack>
                <StatNumber fontSize="3xl" fontWeight="bold" color="blue.500">
                  Rs. {stats.restaurantEarnings.toFixed(2)}
                </StatNumber>
                <StatHelpText>
                  <Text fontSize="sm">Partner payouts</Text>
                </StatHelpText>
              </Stat>
            </MotionFlex>
          </SimpleGrid>
        </CardBody>
      </Card>

      <Card borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor={borderColor} bg={cardBg} overflow="hidden">
        <CardHeader bg={useColorModeValue('gray.50', 'gray.700')} py={4} px={6}>
          <HStack>
            <Icon as={FiShoppingBag as any} color={accentColor} boxSize={5} />
            <Heading as="h4" size="md">
              Settlement Details
            </Heading>
          </HStack>
        </CardHeader>

        <CardBody p={0}>
          <Box overflowX="auto">
            <OrderTable orders={settlements as Settlement[]} orderType="settled" />
          </Box>
        </CardBody>
      </Card>
    </MotionBox>
  );
};

export default SettledOrders;
